import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaProjectDiagram, FaTasks } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../config';
import { getStatusBadgeClass } from '../utils/statusBadge';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getLocalDevSubtasks, isLocalDevCompanyId, LOCAL_DEV_PROJECTS } from '../utils/localDevAuth';
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';

const TeamMember = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [tasksDueToday, setTasksDueToday] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [error, setError] = useState('');
  const [success] = useState('');
  const navigate = useNavigate();
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));
  const effectiveCompanyId = user?.company?.id || localStorage.getItem('company_id') || '1';

  const fetchData = useCallback(async () => {
    try {
      const token = getStoredAccessToken();
      if (isLocalDevSession) {
        const today = new Date().toISOString().slice(0, 10);
        const localProjects = LOCAL_DEV_PROJECTS.filter(project => project.company.id === user.company?.id);
        const localTasks = getLocalDevSubtasks().filter(task => task.major_task.project.company.id === user.company?.id);
        setProjects(localProjects.filter(p => ['ongoing', 'planned'].includes(p.status)));
        setTasksDueToday(localTasks.filter(task => task.due_date?.startsWith(today)));
        setPendingApprovals(localTasks.filter(task => task.assigned_to?.id === user.id && task.status === 'pending_approval'));
        setMyTasks(localTasks.filter(task => task.assigned_to?.id === user.id));
        setError('');
        return;
      }
      if (!token) {
        setError('Session expired. Please log in again.');
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }

      const [projectsRes, dueTodayRes, pendingRes, myTasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/tasks-due-today/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/pending-approvals/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/subtasks/`, {
          headers: getAuthHeaders(token),
          params: { project_status: 'ongoing,planned' },
        }),
      ]);

      const projectsWithTasks = await Promise.all(projectsRes.data.map(async (project) => {
        if (project.major_tasks && project.major_tasks.length > 0) {
          const majorTasks = await Promise.all(project.major_tasks.map(async (taskId) => {
            try {
              const taskRes = await axios.get(`${API_URL}/api/major-tasks/${taskId}/`, {
                headers: getAuthHeaders(token),
              });
              return taskRes.data;
            } catch (error) {
              console.error(`Error fetching major task ${taskId}:`, error);
              return null;
            }
          }));
          return { ...project, major_tasks: majorTasks.filter(task => task !== null) };
        }
        return { ...project, major_tasks: [] };
      }));

      const tasksDueTodayWithDetails = await Promise.all(dueTodayRes.data.map(async (task) => {
        try {
          const taskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, {
            headers: getAuthHeaders(token),
          });
          return { ...task, major_task: taskRes.data };
        } catch (error) {
          console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
          return task;
        }
      }));

      const pendingApprovalsWithDetails = await Promise.all(pendingRes.data.map(async (task) => {
        try {
          const taskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, {
            headers: getAuthHeaders(token),
          });
          return { ...task, major_task: taskRes.data };
        } catch (error) {
          console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
          return task;
        }
      }));

      const myTasksWithDetails = await Promise.all(myTasksRes.data.map(async (task) => {
        try {
          const taskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, {
            headers: getAuthHeaders(token),
          });
          return { ...task, major_task: taskRes.data };
        } catch (error) {
          console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
          return task;
        }
      }));

      const filteredProjects = projectsWithTasks.filter(p => ['ongoing', 'planned'].includes(p.status));
      setProjects(filteredProjects);
      setTasksDueToday(tasksDueTodayWithDetails);
      setPendingApprovals(pendingApprovalsWithDetails);
      setMyTasks(myTasksWithDetails.filter(t => t.assigned_to?.id === user.id));
      setError('');
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: 'Failed to load data. Please try again.',
      }));
    }
  }, [effectiveCompanyId, isLocalDevSession, navigate, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Dashboard - Welcome {user.code || 'Team Member'}</h1>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      {success && <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base">{success}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => navigate('/team-member/projects')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaProjectDiagram className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">Ongoing Projects</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{projects.length}</p>
          </div>
        </div>
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => toggleSection('tasksDueToday')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaTasks className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">Major Tasks Today</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{tasksDueToday.length}</p>
          </div>
        </div>
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => toggleSection('pendingApprovals')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaTasks className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">Send for Approval</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{pendingApprovals.length}</p>
          </div>
        </div>
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => toggleSection('myTasks')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaTasks className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">My Tasks</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{myTasks.length}</p>
          </div>
        </div>
      </div>

      {activeSection === 'tasksDueToday' && (
        <div className="mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">Major Tasks Today</h2>
          {tasksDueToday.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">No major tasks due today.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-2 sm:p-3">Project</th>
                    <th className="p-2 sm:p-3">Major Task</th>
                    <th className="p-2 sm:p-3">Subtask Title</th>
                    <th className="p-2 sm:p-3">Due Date & Time</th>
                    <th className="p-2 sm:p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksDueToday.map(task => (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.due_date ? formatDateTime24(task.due_date) : 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'pendingApprovals' && (
        <div className="mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">Send for Approval</h2>
          {pendingApprovals.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">No tasks pending approval.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-2 sm:p-3">Project</th>
                    <th className="p-2 sm:p-3">Major Task</th>
                    <th className="p-2 sm:p-3">Subtask Title</th>
                    <th className="p-2 sm:p-3">Due Date & Time</th>
                    <th className="p-2 sm:p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingApprovals.map(task => (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.due_date ? formatDateTime24(task.due_date) : 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'myTasks' && (
        <div className="mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">My Tasks</h2>
          {myTasks.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">No tasks assigned to you.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-2 sm:p-3">Project</th>
                    <th className="p-2 sm:p-3">Major Task</th>
                    <th className="p-2 sm:p-3">Subtask Title</th>
                    <th className="p-2 sm:p-3">Due Date & Time</th>
                    <th className="p-2 sm:p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myTasks.map(task => (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.due_date ? formatDateTime24(task.due_date) : 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMember;
